import React from 'react';
import {
  Alert,
  Box,
  Button,
  Input,
  Label,
  Select,
  Spinner,
  Text,
} from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { Plan } from 'commons/services/configurationService';
import {
  PaymentMethodsType,
  PaymentProvidersComparisonParams,
} from 'commons/types';
import {
  formatDecimalMX,
  parseDecimalMX,
  useTranslationWithPrefix,
} from 'commons/utils';

const PAYMENT_METHOD_OPTIONS = [
  PaymentMethodsType.CreditCard,
  PaymentMethodsType.Msi,
  PaymentMethodsType.Ticket,
];

interface FeeCalculatorBodyProps {
  feeCalculatorData: PaymentProvidersComparisonParams;
  handleChangeCalculatorData: (
    key: keyof PaymentProvidersComparisonParams,
    value: string | number,
  ) => void;
  handleCalculate: () => void;
  isLoading: boolean;
  errorKey: string | null;
  plans: Plan[] | undefined;
  isLoadingPlans: boolean;
  errorPlans: unknown;
  availableInstallments: number[];
}

const FeeCalculatorBody: React.FC<FeeCalculatorBodyProps> = ({
  feeCalculatorData,
  handleChangeCalculatorData,
  handleCalculate,
  isLoading,
  errorKey,
  plans,
  isLoadingPlans,
  errorPlans,
  availableInstallments,
}) => {
  const { t } = useTranslationWithPrefix('landing.feeCalculatorModal');

  const isMsiSelected =
    feeCalculatorData.paymentMethodType === PaymentMethodsType.Msi;

  const handlePaymentMethodChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    handleChangeCalculatorData('paymentMethodType', e.target.value);
    if (
      e.target.value === PaymentMethodsType.Msi &&
      availableInstallments.length > 0
    ) {
      handleChangeCalculatorData('installments', availableInstallments[0]);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap="2">
        <Label htmlFor="amount">
          <Text fontWeight="bold" color="neutral-textHigh">
            {t('amountLabel')}
          </Text>
        </Label>
        <Input
          id="amount"
          name="amount"
          type="text"
          inputMode="decimal"
          value={formatDecimalMX(feeCalculatorData.amount)}
          placeholder="0.00"
          append="MXN"
          appendPosition="start"
          appearance={feeCalculatorData.amount <= 0 ? 'danger' : 'neutral'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChangeCalculatorData('amount', parseDecimalMX(e.target.value))
          }
        />
      </Box>

      <Box marginTop="4">
        <FormField.Select
          id="platformPlan"
          name="platformPlan"
          value={feeCalculatorData.tiendanubePlan}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleChangeCalculatorData('tiendanubePlan', e.target.value)
          }
          label={t('platformPlanLabel')}
          required
        >
          {isLoadingPlans ? (
            <Select.Option value="loading" label={t('loadingPlans')} />
          ) : errorPlans ? (
            <Select.Option value="error" label={t('errorLoadingPlans')} />
          ) : (
            plans?.map((plan: Plan) => (
              <Select.Option
                key={plan.externalId}
                value={plan.externalId}
                label={plan.name}
              />
            ))
          )}
        </FormField.Select>
      </Box>

      <Box marginTop="4">
        <FormField.Select
          id="paymentMethod"
          name="paymentMethod"
          value={feeCalculatorData.paymentMethodType}
          onChange={handlePaymentMethodChange}
          label={t('paymentMethodLabel')}
          required
        >
          {PAYMENT_METHOD_OPTIONS.map((value) => (
            <Select.Option
              key={value}
              value={value}
              label={t(`paymentMethodOptions.${value}`)}
            />
          ))}
        </FormField.Select>
      </Box>

      {isMsiSelected && availableInstallments.length > 0 && (
        <Box marginTop="4">
          <FormField.Select
            id="installments"
            name="installments"
            value={String(feeCalculatorData.installments)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleChangeCalculatorData('installments', Number(e.target.value))
            }
            label={t('installmentsLabel')}
            required
          >
            {availableInstallments.map((installment: number) => (
              <Select.Option
                key={installment}
                value={String(installment)}
                label={t('installmentsOption', { count: installment })}
              />
            ))}
          </FormField.Select>
        </Box>
      )}

      {!isLoading && errorKey && (
        <Box marginTop="4">
          <Alert appearance="danger">{t(errorKey)}</Alert>
        </Box>
      )}

      <Box display="flex" gap="3" flexDirection="row" marginTop="4">
        <Button
          appearance="primary"
          onClick={handleCalculate}
          disabled={isLoading}
        >
          {isLoading ? <Spinner size="small" /> : t('calculateButton')}
        </Button>
      </Box>
    </>
  );
};

export default FeeCalculatorBody;
